import { FC } from 'react';
import styled from 'styled-components';
import PROFESSIONAL_SITUATION, {
  ProfessionalSituationValues,
  professionalSituationDetailsLabels,
  professionalSituationDetailsPlaceholders,
  professionalSituationOrder,
  professionalSituationRequiresDetails,
} from 'enums/ProfessionalSituation';
import Error from './Error';
import { FlexColumn } from './generic';
import Input from './Inputs/Input';
import Radio, { getRadioValue } from './Inputs/Radio';

const GroupLabel = styled.p`
  font-weight: 500;
  color: var(--gray-700);
  margin-bottom: 0.25rem;
`;

/** Le champ libre est indenté sous l'option sélectionnée pour montrer le rattachement. */
const DetailsContainer = styled.div`
  margin: 0.25rem 0 0.5rem 1.5rem;
  max-width: 30rem;
`;

const RADIO_NAME = 'professionalSituation';

export type Props = {
  /** Situation sélectionnée. `''` quand rien n'est encore choisi. */
  value: ProfessionalSituationValues | '';
  /** Complément en texte libre (stocké dans `User.organism`). */
  details: string;
  /** Appelé avec la situation et le complément mis à jour de façon cohérente. */
  onChange: (
    value: ProfessionalSituationValues | '',
    details: string,
  ) => void;
  /** Titre du groupe. Passer `null` quand le composant est déjà dans un `Field`. */
  label?: string | null;
  situationError?: boolean;
  detailsError?: boolean;
  readOnly?: boolean;
};

/**
 * Groupe « Quelle est votre situation actuelle ? ».
 *
 * Chaque option peut ouvrir un champ libre dont le libellé dépend de l'option
 * (Entreprise, Organisme, Précisez votre situation, Établissement). Le
 * complément est vidé dès que l'on bascule sur une option qui n'en attend pas,
 * afin de ne jamais enregistrer de résidu de saisie.
 */
const ProfessionalSituationField: FC<Props> = ({
  value,
  details,
  onChange,
  label = 'Quelle est votre situation actuelle ? *',
  situationError,
  detailsError,
  readOnly,
}) => {
  const handleSituationChange: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    const situation = getRadioValue(
      e.target.value,
    ) as ProfessionalSituationValues;
    onChange(
      situation,
      professionalSituationRequiresDetails(situation) ? details : '',
    );
  };

  const handleDetailsChange: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    onChange(value, e.target.value);
  };

  return (
    <FlexColumn gap={0.25}>
      {label && <GroupLabel>{label}</GroupLabel>}
      {situationError && (
        <Error>Vous devez indiquer votre situation actuelle.</Error>
      )}
      {detailsError && !situationError && (
        <Error>
          Vous devez compléter le champ associé à votre situation actuelle.
        </Error>
      )}
      {professionalSituationOrder.map((situation) => (
        <div key={situation}>
          <Radio
            label={PROFESSIONAL_SITUATION[situation]}
            checked={value === situation}
            name={RADIO_NAME}
            id={situation}
            onChange={handleSituationChange}
            readOnly={readOnly}
          />
          {value === situation &&
            professionalSituationRequiresDetails(situation) && (
              <DetailsContainer>
                <Input
                  label={`${professionalSituationDetailsLabels[situation]} *`}
                  placeholder={
                    professionalSituationDetailsPlaceholders[situation]
                  }
                  value={details}
                  onChange={handleDetailsChange}
                  name="organism"
                  error={detailsError}
                  readOnly={readOnly}
                />
              </DetailsContainer>
            )}
        </div>
      ))}
    </FlexColumn>
  );
};

export default ProfessionalSituationField;
